"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: "technical-vocational",
  title: "Accredited Training",
  subtitle: "01",
  image: "https://longshipskills.co.za/wp-content/uploads/2026/10/CALA-RADIO-PRODUCTION_WEB.jpg",
  points: ["Accredited vocational programs", "Hands-on technical workshops", "Industry-aligned curriculum", "Practical skill assessments"],
  overlayClassName: "bg-gradient-to-t md:bg-gradient-to-r from-card/60 via-transparent to-transparent"
}, {
  id: "digital-literacy",
  title: "End User Computing & Digital Literacy",
  subtitle: "02",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/digital_literacy_computing.png",
  points: ["Microsoft Office mastery", "Digital workplace navigation", "Cybersecurity awareness", "Cloud computing basics"]
}, {
  id: "health-safety",
  title: "Occupational Health & Safety",
  subtitle: "03",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/occupational_health_safety.png",
  points: ["Workplace safety compliance", "Risk assessment training", "First aid & emergency response", "Regulatory health standards"]
}, {
  id: "agriculture-production",
  title: "Agriculture, Poultry & Plant Production",
  subtitle: "04",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/agriculture_production_training.png",
  points: ["Sustainable farming methods", "Livestock management skills", "Crop optimization techniques", "Agri-business fundamentals"]
}, {
  id: "media-production",
  title: "Film, Television & Radio Production",
  subtitle: "05",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/media_production_excellence.png",
  points: ["Broadcasting technologies", "Content creation & editing", "Studio equipment operation", "Media project management"]
}, {
  id: "telecom-operations",
  title: "Telecommunications Network Operations",
  subtitle: "06",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/telecom_network_operations.png",
  points: ["Network infrastructure setup", "Fiber optic maintenance", "Wireless system management", "Connectivity troubleshooting"]
}, {
  id: "smme-development",
  title: "SMME & Enterprise Development",
  subtitle: "07",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/smme_enterprise_growth.png",
  points: ["Business strategy planning", "Financial literacy for SMEs", "Market access strategies", "Operational scaling support"]
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.6,
    once: false
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "scale-100 opacity-100 shadow-2xl shadow-primary/20" : "scale-[0.97] opacity-40 shadow-md"}`}>
      <Card className="overflow-hidden border-white/10 bg-card/40 backdrop-blur-xl p-0 rounded-2xl">
        <div className="flex flex-col md:flex-row h-full">
          <div className="w-full md:w-5/12 relative min-h-[250px] md:min-h-[480px] overflow-hidden">
            <div className="absolute inset-0 bg-secondary/20 mix-blend-overlay z-10" />
            <img src={service.image} alt={service.title} className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-1000 ${isVisible ? "scale-105" : "scale-100"}`} loading="lazy" />
            <div className={`absolute inset-0 z-20 pointer-events-none ${service.overlayClassName || "bg-gradient-to-t md:bg-gradient-to-r from-card/95 via-card/50 to-transparent"}`} />
          </div>
          
          <div className="w-full md:w-7/12 p-8 md:p-12 flex flex-col justify-center relative z-30 bg-card/80 md:bg-transparent">
            <span className="text-secondary font-mono text-sm font-bold tracking-widest mb-4 block">
              {service.subtitle}
            </span>
            <h3 className="text-3xl md:text-4xl font-bold mb-8 font-default text-foreground tracking-tight">
              {service.title}
            </h3>
            <ul className="space-y-4 mb-8">
              {service.points.map((point, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                  <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground text-lg leading-relaxed font-default">
                    {point}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </Card>
    </div>;
}
export default function OperativeExzellenzInIzmir() {
  return <section data-section-id="255272" id="the-talent-accelerator" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/10 rounded-2xl blur-[120px]" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-secondary/10 rounded-2xl blur-[120px]" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/mesh_pattern.png')] opacity-[0.05] mix-blend-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
          
          <div className="hidden lg:flex lg:w-1/4 sticky top-32 h-[calc(100vh-16rem)] items-center justify-center">
            <h2 className="text-[8rem] font-bold text-transparent bg-clip-text bg-gradient-to-b from-foreground/10 to-transparent [writing-mode:vertical-rl] rotate-180 tracking-widest uppercase font-default select-none">
              Services
            </h2>
          </div>

          <div className="w-full lg:w-3/4 flex flex-col gap-16 lg:gap-32 py-8 lg:py-32">
            
            <div className="lg:hidden mb-4">
              <span className="text-secondary font-mono text-sm font-bold tracking-widest mb-2 block uppercase">
                The Talent Accelerator
              </span>
              <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground tracking-tighter">
                Our Expertise
              </h2>
              <p className="text-muted-foreground mt-4 text-lg font-default">
                Bridging the gap between professional training and operational excellence since 2017.
              </p>
            </div>

            <div className="flex flex-col gap-16 lg:gap-32">
              {services.map((service, index) => <ServiceCard key={service.id} service={service} index={index} />)}
            </div>

            <div className="mt-8 pt-16 border-t border-border/30 flex flex-col sm:flex-row items-center justify-between gap-8">
              <div>
                <h4 className="text-2xl font-bold font-default mb-2 tracking-tight">Ready to scale your team?</h4>
                <p className="text-muted-foreground font-default">Unlock potential with our tailored training and BPO solutions.</p>
              </div>
              <Button asChild size="lg" className="w-full sm:w-auto group bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-md shadow-lg transition-transform hover:scale-105">
                <Link to="/GetInTouchPage">
                  Get Started Today
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

          </div>
        </div>
      </div>
    </section>;
}